import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowUp, Linkedin, Send } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    
    // Skip ScrollTrigger animations on mobile for better performance
    if (isMobile) {
      const elements = contentRef.current?.querySelectorAll('.animate-item');
      if (elements) {
        gsap.set(elements, { opacity: 1, y: 0 });
      }
      return;
    }
    
    const ctx = gsap.context(() => {
      const elements = contentRef.current?.querySelectorAll('.animate-item');
      if (elements) {
        // Set initial opacity to 1 as fallback to ensure footer is always visible
        gsap.set(elements, { opacity: 1, y: 0 });
        
        gsap.fromTo(
          elements,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            stagger: 0.08,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: footerRef.current,
              start: 'top 90%',
              once: true, // Only animate once - prevents flashing
              toggleActions: 'play none none none', // Never reverse
            },
          }
        );
      }
    }, footerRef);

    return () => ctx.revert();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmail('');
    alert('Thank you for subscribing to KMG insights!');
  };

  const productLinks: Array<{ name: string; href: string }> = [
    { name: 'KMG Vectoral', href: '#vectoral' },
    { name: 'Data Discovery', href: '#product' },
  ];

  const companyLinks: Array<{ name: string; href: string; isRoute?: boolean }> = [
    // { name: 'Core Team', href: '/team', isRoute: true },
    { name: 'Contact', href: '#contact' },
  ];

  const resourceLinks = [
    { name: 'Case Studies', href: '/case-study' },
  ];

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/100204846', label: 'LinkedIn' },
  ];

  return (
    <footer
      ref={footerRef}
      className="relative bg-white overflow-hidden"
    >
      {/* Top Border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div ref={contentRef} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="animate-item">
              <a href="#" className="inline-block mb-6">
                <img 
                  src="/kmg-logo.png" 
                  alt="KMG Logo" 
                  className="h-10 sm:h-12 w-auto object-contain"
                />
              </a>
              <p className="text-slate-600 mb-6 max-w-sm">
                Unlock the value in your data. Built A to Z, tailored to any application. 
                Scale like a tech company.
              </p>
            </div>

            {/* Newsletter */}
            <div className="animate-item">
              <p className="text-sm font-medium text-slate-900 mb-3">
                Subscribe to Data Insights
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-kmg-primary transition-all duration-300 text-sm"
                  required
                />
                <button
                  type="submit"
                  className="px-4 py-3 bg-kmg-primary rounded-xl text-white hover:bg-kmg-primary-dark transition-colors duration-300"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>

          {/* Product Column */}
          <div>
            <h4 className="animate-item text-sm font-semibold text-slate-900 uppercase tracking-wider mb-6">
              Product
            </h4>
            <ul className="space-y-4">
              {productLinks.map((link) => (
                <li key={link.name} className="animate-item">
                  <a
                    href={link.href}
                    className="text-slate-600 hover:text-kmg-primary transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="animate-item text-sm font-semibold text-slate-900 uppercase tracking-wider mb-6">
              Company
            </h4>
            <ul className="space-y-4">
              {companyLinks.map((link) => (
                <li key={link.name} className="animate-item">
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-slate-600 hover:text-kmg-primary transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-slate-600 hover:text-kmg-primary transition-colors duration-300"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="animate-item text-sm font-semibold text-slate-900 uppercase tracking-wider mb-6">
              Resources
            </h4>
            <ul className="space-y-4">
              {resourceLinks.map((link) => (
                <li key={link.name} className="animate-item">
                  <a
                    href={link.href}
                    className="text-slate-600 hover:text-kmg-primary transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="animate-item pt-8 border-t border-slate-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} KMG. All rights reserved.
            </p>
            
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-slate-500 hover:text-slate-900 text-sm transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-500 hover:text-slate-900 text-sm transition-colors duration-300">
                Terms of Service
              </Link>
              <a href="#" className="text-slate-500 hover:text-slate-900 text-sm transition-colors duration-300">
                Security
              </a>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl text-slate-600 hover:bg-kmg-primary hover:text-white transition-all duration-300"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>

            {/* Back to Top */}
            <button
              onClick={scrollToTop}
              className="group flex items-center gap-2 text-slate-500 hover:text-kmg-primary text-sm transition-colors duration-300"
            >
              <span className="hidden sm:inline">Back to Top</span>
              <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl group-hover:bg-kmg-primary group-hover:text-white transition-all duration-300">
                <ArrowUp className="w-5 h-5" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
